/**
 * Filesystem platform adapter (R7.2).
 *
 * Wraps @capacitor/filesystem so all code uses a single, consistent API
 * regardless of platform (Web / Android / iOS).
 *
 * NATIVE (iOS / Android):
 *   Uses Capacitor Filesystem plugin which maps to native file system APIs.
 *   Directory.Data → internal app-private storage (not user-visible).
 *
 * WEB:
 *   @capacitor/filesystem on web persists files via IndexedDB under the hood.
 *   Works in the browser without any additional configuration.
 *   Limitation: browser storage quotas apply (~50 MB default, up to quota on request).
 *
 * Media files (photos / audio) are handled as Base64-encoded strings inside
 * JSON payloads — this avoids dealing with file:// vs blob: URI cross-platform
 * differences and keeps the outbox queue self-contained in a single JSON file.
 */

import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

const BASE_DIR = Directory.Data;

/** Recursively ensure a directory exists (best-effort; ignores "already exists" errors). */
async function ensureDir(path: string): Promise<void> {
  try {
    await Filesystem.mkdir({ path, directory: BASE_DIR, recursive: true });
  } catch {
    // Directory already exists — ignore
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Write a JSON-serialisable value to `path`.
 * Creates parent directories automatically.
 */
export async function writeJson<T>(path: string, data: T): Promise<void> {
  const dir = path.includes('/') ? path.substring(0, path.lastIndexOf('/')) : '';
  if (dir) await ensureDir(dir);
  await Filesystem.writeFile({
    path,
    data: JSON.stringify(data),
    directory: BASE_DIR,
    encoding: Encoding.UTF8,
  });
}

/**
 * Read a JSON file at `path`.
 * Returns `null` if the file does not exist.
 */
export async function readJson<T>(path: string): Promise<T | null> {
  try {
    const result = await Filesystem.readFile({
      path,
      directory: BASE_DIR,
      encoding: Encoding.UTF8,
    });
    const raw = typeof result.data === 'string' ? result.data : await (result.data as Blob).text();
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Append an item to a JSON array file.
 * If the file does not exist, creates it with a single-element array.
 */
export async function appendJsonArray<T>(path: string, item: T): Promise<void> {
  const existing = await readJson<T[]>(path);
  const updated = [...(existing ?? []), item];
  await writeJson(path, updated);
}

/**
 * Delete a file at `path`.
 * Silently ignores "file not found" errors.
 */
export async function deleteFile(path: string): Promise<void> {
  try {
    await Filesystem.deleteFile({ path, directory: BASE_DIR });
  } catch {
    // File already gone — ignore
  }
}

/**
 * List file names in a directory.
 * Returns an empty array if the directory does not exist.
 */
export async function listFiles(path: string): Promise<string[]> {
  try {
    const result = await Filesystem.readdir({ path, directory: BASE_DIR });
    return result.files.map((f) => (typeof f === 'string' ? f : f.name));
  } catch {
    return [];
  }
}

/**
 * Convert a File (from <input type="file">) to a Base64 data string.
 * Used internally by saveMediaFile; avoid storing the result in JSON.
 */
function fileToBase64Internal(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string); // data:mime;base64,...
    reader.onerror = () => reject(new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

/**
 * R-PERF W4-B8: Save a media File to the device filesystem and return its path.
 * The outbox JSON stores only the path, NOT the Base64 data.
 *
 * @param file - The File object to persist.
 * @param subdir - Subdirectory under the app's data dir (e.g. "outbox/media").
 * @returns The relative path (usable with readMediaFile).
 */
export async function saveMediaFile(file: File, subdir: string): Promise<string> {
  await ensureDir(subdir);
  const ext = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
  const filename = `${Date.now()}-${Math.random().toString(36).slice(2)}${ext}`;
  const path = `${subdir}/${filename}`;
  const dataUrl = await fileToBase64Internal(file);
  const base64 = dataUrl.split(',')[1];
  await Filesystem.writeFile({ path, data: base64, directory: BASE_DIR });
  return path;
}

/**
 * R-PERF W4-B8: Read a media file from the device filesystem and return a File object.
 */
export async function readMediaFile(path: string, mimeType: string, filename: string): Promise<File> {
  const result = await Filesystem.readFile({ path, directory: BASE_DIR });
  const base64 = typeof result.data === 'string' ? result.data : await (result.data as Blob).text();
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mimeType });
}

/**
 * @deprecated Use saveMediaFile + readMediaFile instead to avoid storing Base64 in JSON.
 */
export function fileToBase64(file: File): Promise<string> {
  return fileToBase64Internal(file);
}

/**
 * @deprecated Use readMediaFile instead.
 */
export function base64ToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}
